import { useTracker } from '~/composables/tracker';
import $fetch from './fetch';
import { type FCSFile, type DriveFile } from '~/models/file';

type FCSOptions = {
  onUploadProgress?: (name: string, arg1: number) => void;
};

type FCSInformations = {
  actionUrl: string;
  jobticket: string;
  ticket: string;
  csrf: string;
};

const tracker = useTracker();
const sizeOf = (bytes: number) => {
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = ['KO', 'MO', 'GO', 'TO', 'PO', 'EO', 'ZO', 'YO'];
  let u = -1;
  const r = 10 ** 1;

  do {
    bytes /= thresh;
    u += 1;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return `${bytes.toFixed(1)} ${units[u]}`;
};

const transformFilesToBase64: (props: Blob[]) => Promise<unknown[]> = async (files: Blob[]) =>
  Promise.all(files.map(async (e) => transformFileToBase64(e)));

const transformFileToBase64: (props: Blob) => Promise<unknown> = async (file: Blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (fileResult) => {
      const fileBinaryData = fileResult.target?.result;
      resolve({
        name: file.name,
        data: fileBinaryData,
        format: file.name.split('.').pop(),
      });
    };

    reader.readAsDataURL(file);
  });

const getTestPartFile: () => Promise<File> = async () => {
  // Path relative to this .ts file
  const link = new URL('../assets/try-out-mode/Door_Hinge.CATPart', import.meta.url).href;

  const file: any = await $fetch.get(link, { file: true });
  return new File([file as Blob], 'Door_Hinge.CATPart');
};

const _getFCSTicket = async (url: string, tenant: string) => {
  const waf = await $fetch.getWAF();

  return new Promise((resolve, reject) => {
    waf.authenticatedRequest(`${url}/resources/3ddrive/v1/bos/ticket?tenant=${tenant}`, {
      onComplete(response: string, headers: any) {
        resolve({
          ticket: JSON.parse(response).ticket,
          actionUrl: JSON.parse(response).actionurl,
          jobticket: JSON.parse(response).jobticket,
          csrf: headers['x-ds-csrftoken'],
        });
      },
      onFailure(e: Error) {
        tracker?.event('ERROR:3DDrive_getFCSTicket', 'Error', 'Passive');
        reject(Error(e.message));
      },
    });
  });
};

const _checkIn = async (file: File, fcsLoginOptions: FCSInformations, options: FCSOptions) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();

    formData.append(fcsLoginOptions.jobticket, fcsLoginOptions.ticket);
    formData.append('file-name', file.name);
    formData.append('file-title', file.name);
    formData.append('file-description', file.name);
    formData.append('file_0', file);
    formData.append('__fcs__nohtml', 'true');
    const sizeHumanReadable = sizeOf(file.size);

    tracker?.event('TryingToUpload', 'action', 'Conversion', [sizeHumanReadable]);
    $fetch
      .getWAF()
      .then((WAF: any) => {
        WAF.authenticatedRequest(fcsLoginOptions.actionUrl, {
          timeout: 300000, // 5 minutes of timeout
          method: 'POST',
          data: formData,
          onComplete: resolve,
          async onFailure() {
            await tracker?.event('ErrorDuringUploadOfLocalFiles', 'Click on link', 'Conversion');
            reject();
          },
          async onTimeout() {
            await tracker?.event('UploadOfLocalFilesTimeout', 'Click on link', 'Conversion', [
              sizeHumanReadable,
            ]);
            reject();
          },
          onUploadProgress(e: any) {
            if (e.lengthComputable && options?.onUploadProgress) {
              const percentage = Math.round((e.loaded * 100) / e.total);
              options?.onUploadProgress(file.name, percentage);
            }
          },
        });
      })
      .catch((e) => {
        console.log(e);
      });
  });

const _createContentIn3DDrive = async (
  url: string,
  tenant: string,
  csrf: string,
  name: string,
  receipt: any,
): Promise<DriveFile> =>
  new Promise((resolve, reject) => {
    const data = {
      type: 'DriveFile',
      title: name,
      receipt,
    };
    $fetch
      .getWAF()
      .then((WAF: any) => {
        WAF.authenticatedRequest(
          `${url}/resources/3ddrive/v1/bos/DSROOT/contents?tenant=${tenant}&update=true`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-DS-CSRFTOKEN': csrf,
            },
            type: 'json',
            data: JSON.stringify(data),
            onComplete: resolve,
            onFailure() {
              reject(new Error('3DDrive Upload failed'));
            },
          },
        );
      })
      .catch((e) => {
        console.log(e.message);
      });
  });

const isFCSFile = (item: FCSFile | DriveFile): item is FCSFile => !('type' in item);

const uploadFilesToFCS = async (
  files: File[],
  onProgressCb?: (progress: number) => void,
): Promise<FCSFile[]> => {
  const uploadedFiles: FCSFile[] = [];
  const totalSize = files.reduce((tot, curr) => tot + curr.size, 0);
  const currentSize: Record<string, number> = {};
  // Uploaded size for all files combined
  const calculateProgress = (): number => {
    const uploadedSize = Object.values(currentSize).reduce<number>((acc, cur) => acc + cur, 0);
    return (uploadedSize / totalSize) * 100;
  };

  await Promise.all(
    files.map(async (f) => {
      const formData = new FormData();
      formData.append('file', f, f.name);
      try {
        const filesObj = (await $fetch.post('/resources/mdcart/documents/3dpart', {
          query: { service: '3dprinting' },
          form: formData,
          timeout: 360000,
          onUploadProgress(e: ProgressEvent) {
            currentSize[f.name] = e.loaded;
            const size = calculateProgress();
            if (onProgressCb) {
              onProgressCb(Math.min(size, 100));
            }
          },
        })) as Record<string, string>;

        Object.entries(filesObj).forEach(([filename, id]) => {
          uploadedFiles.push({
            id,
            title: filename,
            extension: filename.split('.').pop() || '',
            tenant: window.ds.tenant,
          });
        });
      } catch (error: any) {
        tracker?.error(
          'ordering',
          'HIGH',
          error.message || 'unknown',
          'fcs_upload_error',
          [f.name, f.type],
          [f.size],
        );
        throw new Error(`Error when uploading file : ${f.name},  | ${error.message}`);
      }
    }),
  );

  return uploadedFiles;
};

const uploadToFcsWith3DDriveFallback = async (
  files: File[],
  onProgressCallback?: (progress: number) => void,
): Promise<DriveFile[] | FCSFile[]> => {
  let filesWithIds = [];
  try {
    filesWithIds = await uploadFilesToFCS(files, (progress: number) => {
      if (onProgressCallback) {
        onProgressCallback(progress);
      }
    });
    return filesWithIds;
  } catch (error) {
    console.error('Failed uploading with FCS, starting to fallback to 3DDrive\n', error);

    filesWithIds = await uploadFilesTo3DDrive(files, (progress: number) => {
      if (onProgressCallback) {
        onProgressCallback(progress);
      }
    });
    return filesWithIds;
  }
};

const uploadFilesTo3DDrive = async (
  files: File[],
  onProgressCallback?: (progress: number) => void,
): Promise<DriveFile[]> => {
  const totalSize = files.length * 100;
  const currentSize: Record<string, number> = {};
  const calculateProgress = (): number => {
    const uploadedSize = Object.values(currentSize).reduce<number>((acc, cur) => acc + cur, 0);
    return (uploadedSize / totalSize) * 100;
  };

  const uploadedFiles = await Promise.all<any>(
    files.map(async (f) =>
      uploadFileTo3DDrive(f, {
        onUploadProgress(name, progress) {
          currentSize[name] = progress;
          const size = calculateProgress();
          if (onProgressCallback) {
            onProgressCallback(Math.min(size, 100));
          }
        },
      }),
    ),
  );

  return uploadedFiles;
};

const uploadFileTo3DDrive = async (file: File, options: FCSOptions = {}): Promise<DriveFile> => {
  const driveURL = await $fetch.getServiceURL('3DDrive');
  const tenant = await $fetch.getTenant();

  const files = _upload(file, driveURL!, tenant, options);
  //	Tracker.event('PERF:UPLOADFILES', 'Performance', 'Passive', [String(performance.now() - t0)]);

  return files;
};

const _upload = async (
  file: File,
  url: string,
  tenant: string,
  options: FCSOptions,
  _isRetry = false,
): Promise<DriveFile> => {
  const fcsLoginOptions = (await _getFCSTicket(url, tenant)) as FCSInformations;
  const response: any = await _checkIn(file, fcsLoginOptions, options);

  return _createContentIn3DDrive(url, tenant, fcsLoginOptions.csrf, file.name, response.trim());
};

export {
  getTestPartFile,
  transformFilesToBase64,
  uploadToFcsWith3DDriveFallback,
  uploadFileTo3DDrive,
  uploadFilesToFCS,
  sizeOf,
  isFCSFile,
};
